/**
 * Paper-journal upload CSV format — keep in sync with
 * rewards-service `PaperJournalCsvRow` and `_REQUIRED_CSV_COLUMNS`.
 */

export interface PaperJournalCsvColumnSpec {
  /** Canonical header name (first row of the CSV). */
  header: string;
  required: boolean;
  description: string;
  example: string;
}

/** Columns the API validates on every row (see `app/schemas/v1/paper_journal.py`). */
export const PAPER_JOURNAL_CSV_COLUMNS: readonly PaperJournalCsvColumnSpec[] = [
  {
    header: 'RefUserId',
    required: true,
    description: 'Internal rewards user id (positive integer).',
    example: '45787',
  },
  {
    header: 'EligibleParticipantId',
    required: true,
    description: 'InComm eligible participant id (EPID).',
    example: '86298',
  },
  {
    header: 'PartnerProductId',
    required: true,
    description: 'Partner product id for purse / offer resolution.',
    example: '241',
  },
  {
    header: 'PointsAmount',
    required: true,
    description: 'Reward amount in points (positive whole number).',
    example: '1000',
  },
  {
    header: 'FirstName',
    required: true,
    description: 'Cardholder first name (used for InComm demo provisioning).',
    example: 'Jane',
  },
  {
    header: 'LastName',
    required: true,
    description: 'Cardholder last name.',
    example: 'Journal',
  },
  {
    header: 'AddressLine1',
    required: true,
    description: 'Mailing address line 1.',
    example: '123 Main St',
  },
  {
    header: 'AddressLine2',
    required: false,
    description: 'Optional second address line; leave blank if unused.',
    example: '',
  },
  {
    header: 'City',
    required: true,
    description: 'City.',
    example: 'Austin',
  },
  {
    header: 'State',
    required: true,
    description: 'State / province code.',
    example: 'TX',
  },
  {
    header: 'ZipCode',
    required: true,
    description: 'Postal / ZIP code.',
    example: '78701',
  },
  {
    header: 'DateOfBirth',
    required: true,
    description: 'Date of birth as MM/DD/YYYY (e.g. 01/15/1980) or YYYY-MM-DD.',
    example: '01/15/1980',
  },
] as const;

/** Exact header row expected by the upload parser (sample_data/paper_journal_sample.csv). */
export const PAPER_JOURNAL_CSV_HEADER_LINE = PAPER_JOURNAL_CSV_COLUMNS.map((c) => c.header).join(',');
